import { Typography } from "@suraasa/placebo-ui"
import { Enrollment } from "api/resources/learningItems/types"
import clsx from "clsx"
import { Link, useLocation, useParams } from "react-router-dom"
import routes from "routes"
import { useEnrollments } from "utils/hooks/useEnrollments"
import { PropsWithClassName } from "utils/types"

type Crumb = {
  id?: string
  path: string
  displayName: string
}

export type BreadcrumbProps = PropsWithClassName<{
  skipLastItem?: boolean
  color?: "light" | "dark"
}>

const buildCrumbs = ({
  parent,
  item,
  skipLastItem,
}: {
  parent: Enrollment | undefined
  item: Enrollment | undefined
  skipLastItem: boolean
}): Crumb[] => {
  const crumbs: Crumb[] = [
    { id: "learning", path: routes.learning, displayName: "Learning" },
  ]

  if (!item) return crumbs

  if (parent) {
    crumbs.push({
      path: `/${parent.type}/${parent.slug}`,
      displayName:
        // `slice(0, 15)` + the ellipsis renders 18 chars, so truncating
        // anything shorter than that would make the crumb longer, not shorter.
        skipLastItem || parent.name.length <= 18
          ? parent.name
          : // Truncate the item to save space
            `${parent.name.slice(0, 15)}...`,
    })
    crumbs.push({
      path: `/${parent.type}/${parent.slug}/${item.type}/${item.slug}`,
      displayName: `${item.name}`,
    })
  } else {
    crumbs.push({
      path: `/${item.type}/${item.slug}`,
      displayName: `${item.name}`,
    })
  }

  if (skipLastItem) return crumbs.slice(0, -1)

  return crumbs
}

const Breadcrumbs = ({
  className,
  color = "dark",
  skipLastItem = true,
}: BreadcrumbProps) => {
  const params = useParams()

  const { state } = useLocation()

  const slug = state?.slug || params.slug

  const { enrollments } = useEnrollments()
  if (!enrollments) return null

  const enrollment = enrollments.find(enrollment => enrollment.slug === slug)

  // Fall back to the enrollment's own parent chain when the caller didn't
  // explicitly pass a parentSlug (e.g. navigating from schedule/calendar
  // views that only know the item's own slug).
  const parentSlug =
    state?.parentSlug || params.parentSlug || enrollment?.parents?.[0]?.slug

  const parentEnrollment = enrollments.find(
    enrollment => enrollment.slug === parentSlug && enrollment.type !== "course"
  )

  const breadCrumbs = buildCrumbs({
    item: enrollment,
    parent: parentEnrollment,
    skipLastItem: skipLastItem,
  })

  return (
    <div
      className={clsx("mb-1 flex flex-wrap items-center", className, {
        "text-common-white-500": color === "light",
        "text-common-black-500": color === "dark",
      })}
    >
      {breadCrumbs.map((crumb, index) => {
        return (
          <div key={index} className="flex items-center whitespace-nowrap">
            <Link to={crumb.path} className="hover:underline">
              <Typography
                variant="smallBody"
                className="whitespace-pre-line break-words"
              >
                {crumb.displayName} <span className="mx-1">/</span>
              </Typography>
            </Link>
          </div>
        )
      })}
    </div>
  )
}

export default Breadcrumbs
